import React, { ReactNode, useContext, useState } from 'react';

import { useHistory, useLocation } from 'react-router-dom';

import { Team } from '@/core/api/collections/teams';
import { getUsers, inviteUsers } from '@/core/api/collections/users';
import { AuthContext } from '@/core/auth/AuthContext';
import {
  sanitizeCommaSeparatedEmailInput,
  UserInviteError,
  UserInviteErrors,
  UserInviteFormData,
  validateUserInvite,
} from '@/core/users/UserInvite/userInviteValidation';
import { DEFAULT_CORE_PERMISSIONS } from '@/core/users/UserPermissions/constants';
import { calculatePerAdminBillingRateCents } from '@/core/utils/account';
import routes from '@/routes';
import { useSnackbar } from '@/SnackbarProvider';

export interface UserInviteCtx {
  inviteParams: UserInviteFormData;
  setInviteParams: (params: UserInviteFormData) => void;
  selectedTeams: { [key: number]: Team | undefined };
  updateTeam: (team: Team, isSelected: boolean) => void;
  errors: UserInviteErrors;
  validateEmails: () => void;
  isValidating: boolean;
  setIsValidating: (isValidating: boolean) => void;
  submitCount: number;
  sendInvite: () => void;
  billingRate?: number;
  showConfirmModal: boolean;
  toggleConfirmModal: () => void;
}

export const UserInviteContext = React.createContext({} as UserInviteCtx);

type GetDefaultFormData = (replyTo: string, hasCoreAccess: boolean, hasUvkAccess: boolean) => UserInviteFormData;
const getDefaultFormData: GetDefaultFormData = (replyTo, hasCoreAccess, hasUvkAccess) => ({
  core: hasCoreAccess ? {
    enabled: true,
    ...DEFAULT_CORE_PERMISSIONS,
  } : undefined,
  uvk: hasUvkAccess ? {
    enabled: true,
    admin: true,
    state: 'invited',
  } : undefined,
  emails: [],
  commaSeparatedEmails: '',
  replyTo,
});

type Props = {
  children: ReactNode;
}
export const UserInviteContextProvider = ({ children }: Props) => {
  const history = useHistory();
  const { enqueueSnackbar, enqueueSnackbarError } = useSnackbar();
  const { currentAccount, currentUser, hasCoreAccess, hasUvkAccess, licensesRemaining, updateBillingRate } = useContext(AuthContext);
  const [selectedTeams, setSelectedTeams] = useState({} as { [key: number]: Team | undefined });
  const [isValidating, setIsValidating] = useState(false);
  const [submitCount, setSubmitCount] = useState(0);
  const [billingRate, setBillingRate] = useState<number | undefined>(undefined);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const initialData = getDefaultFormData(
    currentUser!.email,
    !!hasCoreAccess && currentUser!.coreOwner,
    !!hasUvkAccess && currentUser!.uvkAdmin);
  const currentUrlParams = new URLSearchParams(useLocation().search);
  const emailParam = currentUrlParams.get('email');
  if (emailParam) {
    initialData.commaSeparatedEmails = sanitizeCommaSeparatedEmailInput(emailParam);
  }
  const [inviteParams, setInviteParams] = useState(initialData);
  const [errors, setErrors] = useState(initialData.commaSeparatedEmails ? {} : { emails: UserInviteError.EMAILS_REQUIRED } as UserInviteErrors);

  const validateEmails = () => {
    const emails = sanitizeCommaSeparatedEmailInput(inviteParams.commaSeparatedEmails);
    if (!emails) return;

    setIsValidating(true);
    getUsers({ per_page: 1, emails }).then(([users]) => {
      if (users?.length) {

        const licenseType = users[0].permissions?.core?.licenseType;

        if (licenseType === 'contributor') {
          setErrors({...errors, emails: `${users[0].email} is already a contributor`});
        } else {
          setErrors({...errors, emails: `${users[0].email} is already an administrator`});
        }
      }
    }).catch((e) => {
      enqueueSnackbarError(e);
    }).finally(() => setIsValidating(false));
  };

  const setInviteParamsHandler = (params: UserInviteFormData) => {
    setInviteParams({ ...params });
    const updatedErrors = validateUserInvite(inviteParams, licensesRemaining!);
    setErrors(updatedErrors);
    updateBilling(updatedErrors);
  };

  const updateBilling = (updatedErrors: UserInviteErrors) => {
    if (inviteParams.core?.state === 'active') {
      const emails = inviteParams.commaSeparatedEmails.replaceAll(' ', '').split(',');
      const hasErrors = Object.keys(updatedErrors).length;
      if (!emails.length || hasErrors) {
        setBillingRate(undefined);
      } else if (emails.length) {
        const newBillingRate = calculatePerAdminBillingRateCents(currentAccount!, emails.length);
        if (newBillingRate) {
          setBillingRate(newBillingRate);
        }
      }
    } else {
      setBillingRate(undefined);
    }
  };

  const updateTeam = (team: Team, isSelected: boolean) => {
    if (isSelected) {
      selectedTeams[team.id] = team;
    } else {
      selectedTeams[team.id] = undefined;
    }
    setSelectedTeams({ ...selectedTeams });
  };

  const sendInvite = () => {
    if (Object.keys(errors).length === 0) {
      inviteParams.emails = sanitizeCommaSeparatedEmailInput(inviteParams.commaSeparatedEmails).split(/,\s*/);
      const addedTeams = Object.keys(selectedTeams).map(id => parseInt(id));
      setShowConfirmModal(false);
      inviteUsers(inviteParams, addedTeams).then(() => {
        if (billingRate) {
          updateBillingRate(billingRate);
        }
        // after a successful response, send them to the users list
        history.push(routes.users.self);
        setTimeout(() => enqueueSnackbar('Invite(s) sent!', { variant: 'success' }), 800);
      }).catch(e => {
        e.response?.data?.errors?.forEach((error: string) => {
          enqueueSnackbar(`Error: ${error}`, { variant: 'error' });
        });
      });
    }
    setSubmitCount(submitCount + 1);
  };

  const toggleConfirmModal = () => {
    setShowConfirmModal(!showConfirmModal);
  };

  return (
    <UserInviteContext.Provider
      value={{
        inviteParams,
        setInviteParams: setInviteParamsHandler,
        selectedTeams,
        updateTeam,
        errors,
        validateEmails,
        isValidating,
        setIsValidating,
        submitCount,
        sendInvite,
        billingRate,
        showConfirmModal,
        toggleConfirmModal,
      }}
    >
      {children}
    </UserInviteContext.Provider>
  );
};
